import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Modal, ModalBody, Row, Col } from "reactstrap";
import i18next from "i18next";
import { BsDownload } from "react-icons/bs";

import { requestFileUploadUrl, barcodeQtyImportReset } from "actions/barcodeQtyImportActions.js";

import { currentUserDataSelector } from "selectors/currentUserSelector";
import { buyplanIdSelector, buyplanStateSelector } from "selectors/buyplanIdSelector.js";
import { bannerInfoDataSelector } from "selectors/bannerInfoSelector.js";
import { releaseMultipleMonthsSelector } from "selectors/buyplanInfoSelector.js";
import { stockModelIdSelector } from "selectors/stockModelIdSelector";
import { warehouseIdSelector } from "selectors/warehouseSelector.js";

import {
  fileUploadStatusSelector,
  barcodeQtyImportProgressSelector,
  barcodeQtyImportIsProcessingSelector
} from "selectors/barcodeQtyImportSelector.js";

import { allowBarcodeQtyImportSelector } from "selectors/filtersSelector.js";

import BarcodeFileUploadForm from "./form.jsx";
import ImportProgress from "./importProgress.jsx";

import { shouldUserUpdateQty } from "utils.js";
import { addNotification } from "notificationStore";

const BarcodeQtyImport = () => {

  const dispatch = useDispatch();

  const currentUser = useSelector(currentUserDataSelector);
  const buyplanState = useSelector(buyplanStateSelector);
  const bannerInfoData = useSelector(bannerInfoDataSelector);
  const releaseStatus = bannerInfoData.get("release_status");

  const allowBarcodeQtyImport = useSelector(allowBarcodeQtyImportSelector);

  const confirmedBuyplanId = useSelector(buyplanIdSelector);
  const confirmedStockModalId = useSelector(stockModelIdSelector);
  const warehouseId = useSelector(warehouseIdSelector);

  const isFileUploadedSuccesfully = useSelector(fileUploadStatusSelector);
  const importProgress = useSelector(barcodeQtyImportProgressSelector);
  const isImportProcessing = useSelector(barcodeQtyImportIsProcessingSelector);

  const isMultipleMonthsForRelease = useSelector(releaseMultipleMonthsSelector);

  const defaultFormData = {
    file: null,
    barcode: "",
    quantity: "",
    delivery_month: ""
  };

  const [isModalOpen, setModalOpen] = useState(false);
  const [fileHeaders, setFileHeaders] = useState([]);
  const [formData, setFormData] = useState(defaultFormData);
  const [buttonText, setButtonText] = useState(() => i18next.t("barcodeImport.upload"));
  const [showUploadingText, setShowUploadingText] = useState(false);

  useEffect(() => {

    if (
      isFileUploadedSuccesfully &&
      importProgress?.stage === "upload_complete" &&
      importProgress?.status === "success"
    ) {
      const timer = setTimeout(() => {
        closeModal();
      }, 2000);

      return () => clearTimeout(timer);
    }

  }, [isFileUploadedSuccesfully, importProgress?.stage, importProgress?.status]);

  const openModal = () => {
    setModalOpen(true);
    dispatch(barcodeQtyImportReset());
  };

  const closeModal = () => {
    setModalOpen(false);
    setButtonText(i18next.t("barcodeImport.upload"));
    setFormData(defaultFormData);
    setFileHeaders([]);
  };

  const isFormValid = () => {
    return (
      formData.file !== null &&
      formData.barcode.trim() !== "" &&
      formData.quantity.trim() !== ""
    );
  };

  const handleSubmit = (evt) => {

    evt.preventDefault();

    setButtonText(i18next.t("barcodeImport.uploading"));
    setShowUploadingText(true);

    const fileExtension = formData.file.name.split(".").pop().toLowerCase();

    if (!["csv", "xlsx", "xls"].includes(fileExtension)) {

      addNotification({
        type: "danger",
        message: i18next.t("barcode.invalidFileFormat")
      });

      return;
    }

    dispatch(
      requestFileUploadUrl({
        file: formData.file,
        file_name: formData.file.name,
        barcode: formData.barcode,
        quantity: formData.quantity,
        delivery_month: formData.delivery_month,
        buyplan_id: confirmedBuyplanId,
        stock_model_id: confirmedStockModalId,
        warehouse_id: warehouseId,
        headers: fileHeaders
      })
    );

    setFormData({
      ...formData,
      barcode: "",
      quantity: "",
      delivery_month: "",
      file: null
    });

  };

  const shouldCurrentUserUpdateQty = shouldUserUpdateQty(
    confirmedBuyplanId,
    confirmedStockModalId,
    currentUser.get("role"),
    buyplanState,
    releaseStatus
  );

  const shouldMapDeliveryMonths = confirmedBuyplanId && isMultipleMonthsForRelease;

  return (
    <>
      {allowBarcodeQtyImport && shouldCurrentUserUpdateQty && (
        <Button
          className="w-75"
          size="sm"
          style={{ marginLeft: "20px", marginBottom: "10px" }}
          color="primary"
          onClick={openModal}
          title={i18next.t("buttons.importQty")}
        >
          <BsDownload /> {i18next.t("buttons.importQty")}
        </Button>
      )}

      {isModalOpen && (
        <Modal
          className="modal-container"
          isOpen={isModalOpen}
          centered
          autoFocus={false}
          backdrop="static"
          size="xl"
        >
          <div
            className="modal-header align-items-center"
            style={{ gap: 12, paddingTop: 5, paddingBottom: 3}}
          >
            <h5 className="modal-title-barcode-import" style={{ whiteSpace: "nowrap" }}>
              {i18next.t("barcodeImport.title")}
            </h5>

            {showUploadingText && <div
              className="alert alert-info py-1 px-3 mb-0 mx-2 flex-grow-1"
              style={{
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              You can leave this page and an email will be sent to notify you when the upload is complete.
            </div>}

            {/* Buttons wrapper */}
            <div className="d-flex align-items-center ml-auto" style={{ gap: 8 }}>
              <Button
                color="secondary"
                size="sm"
                onClick={handleSubmit}
                disabled={!isFormValid() || isImportProcessing}
              >
                {buttonText}
              </Button>

              <Button
                color="primary"
                size="sm"
                onClick={closeModal}
              >
                {i18next.t("barcodeImport.exit")}
              </Button>
            </div>
          </div>

          <ModalBody>
            <Row className="align-items-start">
              <Col lg="6" className="pr-lg-4">
                <BarcodeFileUploadForm
                  fileHeaders={fileHeaders}
                  setFileHeaders={setFileHeaders}
                  formData={formData}
                  setFormData={setFormData}
                  shouldMapDeliveryMonths={shouldMapDeliveryMonths}
                  disabled={isImportProcessing}
                />
              </Col>

              <Col
                lg="6"
                className="pl-lg-4 mt-4 mt-lg-0"
                style={{
                  borderLeft: "1px solid #e6e6e6",
                  paddingLeft: 28
                }}
              >
                {showUploadingText &&  <ImportProgress
                  stage={importProgress?.stage}
                  status={importProgress?.status}
                  total_rows={importProgress?.total_rows}
                  processed_rows={importProgress?.processed_rows}
                  progress_percentage={importProgress?.progress_percentage}
                  error_message={importProgress?.error_message}
                />}
              </Col>
            </Row>
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

export default React.memo(BarcodeQtyImport);
